import { usableFoldedPersonName } from '@steward-app/steward-types';
import type { UserProfile } from '../api/types';

/** Title-case an email local part for display, e.g. "matt.anderson" → "Matt Anderson". */
function titleCaseLocalPart(local: string): string {
  return local
    .split(/[.\-_+]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

const emailLocalPart = (email?: string) => (email ?? '').split('@')[0] ?? '';

/**
 * Whether the user has a *real* name on file (not just the email-derived fallback
 * appcore mints at signup when Cognito collected no given/family name). Drives the
 * one-time "what should we call you?" nudge.
 */
export function hasRealName(profile: UserProfile | null): boolean {
  if (!profile) return false;
  if (profile.givenName?.trim()) return true;
  const name = profile.name?.trim();
  return !!name && name !== emailLocalPart(profile.email);
}

/**
 * What to greet the user by: their preferred name verbatim (a first name, nickname,
 * or formal "Mr. Anderson") → first word of the full name → title-cased email local
 * part. Empty string when nothing is known.
 */
export function greetingName(profile: UserProfile | null): string {
  if (!profile) return '';
  const preferred = profile.givenName?.trim();
  if (preferred) return preferred;

  const name = profile.name?.trim();
  const local = emailLocalPart(profile.email);
  if (name && name !== local) return name.split(/\s+/)[0]!;

  const titled = titleCaseLocalPart(local);
  return titled ? titled.split(/\s+/)[0]! : '';
}

/**
 * The first name the onboarding welcome may greet a client by, or `undefined` for a plain
 * "Welcome" (crossmarc/steward-app#2450). Unlike {@link greetingName} it NEVER falls back to
 * the email: appcore mints `name` as the email's local part at signup when the sign-in
 * collected no given or family name (see {@link hasRealName}), and a client who arrives by
 * invite is exactly that case, so the first word of `name` was the whole local part
 * ("Welcome, v3snap-mvbpxanj-39844b"). A candidate is refused when it contains an "@", or when
 * it is the email's local part (compared case-insensitively, with `.`, `-`, `_`, `+` and
 * spaces treated alike, so "Jane Doe" is refused for jane.doe@ and "jane" is not refused for
 * jane.doe@). The preferred `givenName` wins, then the first word of `name`.
 */
export function onboardingGreetingName(profile: Pick<UserProfile, 'email' | 'name' | 'givenName'> | null | undefined): string | undefined {
  if (!profile) return undefined;
  const usable = (candidate: string | undefined): string | undefined => usableFoldedPersonName(candidate, profile.email);
  const preferred = usable(profile.givenName);
  if (preferred) return preferred;
  // The first word of a full name is checked on its own: "jane Smith" for jane@ greets nobody.
  return usable(usable(profile.name)?.split(/\s+/)[0]);
}

/**
 * The best guess at a *full* display name, for seeding an editable field (not for
 * silent display — see `greetingName` for that). Same precedence as `greetingName`
 * but keeps every word, e.g. "matt.anderson" → "Matt Anderson" rather than "Matt".
 * Empty string when nothing is known.
 */
export function derivedFullName(profile: UserProfile | null): string {
  if (!profile) return '';
  const preferred = profile.givenName?.trim();
  if (preferred) return preferred;

  const name = profile.name?.trim();
  const local = emailLocalPart(profile.email);
  if (name && name !== local) return name;

  return titleCaseLocalPart(local);
}

/**
 * The "what should we call you?" ask (F13), reframed as a confirmation of the
 * derived guess when one exists rather than an open-ended question — shared by
 * WelcomeOverlay and NameNudge so the two surfaces speak with one voice instead
 * of drifting apart (as they had: NameNudge shipped the old open question, with
 * an em dash, well after WelcomeOverlay was fixed to confirm instead of ask).
 */
export function nameAskLabel(profile: UserProfile | null): string {
  const greetName = greetingName(profile);
  return greetName
    ? `We'll call you ${greetName}. Change it if you'd rather be called something else.`
    : 'What should we call you?';
}

/**
 * Splits a confirmed/edited name into the two fields the profile write needs:
 * `givenName` (first word only — drives the short greeting everywhere, e.g. the
 * Dashboard headline) and `name` (the full string as typed — drives the account
 * menu). Shared by WelcomeOverlay and NameNudge for the same reason as
 * `nameAskLabel` above: NameNudge once prefilled the FULL derived guess (e.g.
 * "Matt Anderson") without inheriting WelcomeOverlay's split, so accepting the
 * one-click default silently wrote the entire string into `givenName`. Call this
 * on the trimmed, non-empty value at submit time in both places so the two
 * surfaces can't diverge on write behavior the way they already diverged on copy.
 */
export function splitConfirmedName(trimmed: string): { givenName: string; name: string } {
  const givenName = trimmed.split(/\s+/)[0] ?? trimmed;
  return { givenName, name: trimmed };
}

/** The full display name for the account menu / footer (name → email → "Account"). */
export function accountDisplayName(profile: UserProfile | null): string {
  return profile?.name?.trim() || profile?.email || 'Account';
}

/** Up-to-two-letter initials from the name, falling back to the email local part. Empty while the profile is still loading. */
export function accountInitials(profile: UserProfile | null): string {
  if (!profile) return '';
  const source = profile.name?.trim() || emailLocalPart(profile.email);
  if (!source) return '';
  const parts = source.split(/[.\-_+\s]+/).filter(Boolean);
  const initials = parts.slice(0, 2).map(p => p[0]!.toUpperCase()).join('');
  return initials || source.slice(0, 2).toUpperCase();
}
